import { useMemo } from 'react';
import {
	QUERY_PARAM_SORT_KEY,
	QUERY_PARAM_SORT_ORDER,
	QUERY_PARAM_SUMMARY,
	QUERY_PARAM_AUTHORS,
	QUERY_PARAM_REVIEWERS,
	QUERY_PARAM_STATUS,
	QUERY_PARAM_REPOSITORIES,
} from '../../common/constants';
import {
	usePullRequestTableOptionsStore,
	initialPullRequestTableOptions,
} from '../pull-request-filters';
import type { PullRequestTableOptions } from '../pull-request-filters/types';
import type { QueryParams, SearchQueryParams } from './types';

const DELIMITER = ',';
const ENCODED_DELIMITER = encodeURIComponent(DELIMITER);

// We encode the delimiter to protect the values from containing
// the same character that delimits the list.
// e.g.
// env names = ["prod,east", "prod,west"] -> "prod,east,prod,west"
const encodeDelimiter = (input: string) => {
	const regex = new RegExp(DELIMITER, 'g');
	return input.replace(regex, ENCODED_DELIMITER);
};

const decodeDelimiter = (input: string) => {
	const regex = new RegExp(ENCODED_DELIMITER, 'g');
	return input.replace(regex, DELIMITER);
};

const arrayFilterToQueryParam = (filterArray: string[]) => {
	if (filterArray.length === 0) return undefined;

	return filterArray.map((entity) => encodeDelimiter(entity)).join(DELIMITER);
};

const arrayQueryParamToFilter = (queryParamString: string) =>
	queryParamString.split(DELIMITER).map(decodeDelimiter);

export const getPullRequestTableOptionsFromQueryParams = (
	queryParams: SearchQueryParams,
): PullRequestTableOptions => {
	const state: PullRequestTableOptions = {
		...initialPullRequestTableOptions,
	};

	if (queryParams[QUERY_PARAM_SUMMARY] != null) {
		state.titleFilter = queryParams[QUERY_PARAM_SUMMARY];
	}

	if (queryParams[QUERY_PARAM_AUTHORS] != null) {
		state.authorsFilter = arrayQueryParamToFilter(queryParams[QUERY_PARAM_AUTHORS]);
	}

	if (queryParams[QUERY_PARAM_REVIEWERS] != null) {
		state.reviewersFilter = arrayQueryParamToFilter(queryParams[QUERY_PARAM_REVIEWERS]);
	}

	if (queryParams[QUERY_PARAM_STATUS] != null) {
		state.statusFilter = arrayQueryParamToFilter(queryParams[QUERY_PARAM_STATUS]);
	}

	if (queryParams[QUERY_PARAM_REPOSITORIES] != null) {
		state.repositoryFilter = arrayQueryParamToFilter(queryParams[QUERY_PARAM_REPOSITORIES]);
	}

	if (queryParams[QUERY_PARAM_SORT_KEY] != null) {
		state.sortKey = queryParams[QUERY_PARAM_SORT_KEY];
	}

	if (queryParams[QUERY_PARAM_SORT_ORDER] != null) {
		state.sortOrder = queryParams[QUERY_PARAM_SORT_ORDER];
	}

	return state;
};

export const getQueryParamsFromPullRequestTableOptions = (
	filtersAndSorts: PullRequestTableOptions,
): QueryParams => ({
	[QUERY_PARAM_SUMMARY]:
		filtersAndSorts.titleFilter === '' ? undefined : filtersAndSorts.titleFilter,
	[QUERY_PARAM_AUTHORS]: arrayFilterToQueryParam(filtersAndSorts.authorsFilter),
	[QUERY_PARAM_REVIEWERS]: arrayFilterToQueryParam(filtersAndSorts.reviewersFilter),
	[QUERY_PARAM_STATUS]: arrayFilterToQueryParam(filtersAndSorts.statusFilter),
	[QUERY_PARAM_REPOSITORIES]: arrayFilterToQueryParam(filtersAndSorts.repositoryFilter),
	[QUERY_PARAM_SORT_KEY]: filtersAndSorts.sortKey === '' ? undefined : filtersAndSorts.sortKey,
	[QUERY_PARAM_SORT_ORDER]:
		filtersAndSorts.sortOrder === '' ? undefined : filtersAndSorts.sortOrder,
});

export const useQueryParamsFromFiltersAndSorts = (): QueryParams => {
	const [pullRequestTableOptions] = usePullRequestTableOptionsStore();
	return useMemo(
		() => getQueryParamsFromPullRequestTableOptions(pullRequestTableOptions),
		[pullRequestTableOptions],
	);
};
